import { cn } from "./classNames-BzrkDnOD.js";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/blocks/PageHeader/styles.ts
const titleRowClass = "tw:group/title-row tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2 tw:has-[[data-slot=inline-edit]]:items-start";
const titleLineClass = "tw:group-has-[[data-slot=inline-edit]]/title-row:min-h-9";
const titleCompactLineClass = "tw:group-has-[[data-slot=inline-edit]]/title-row:min-h-7";
const titleGroupClass = "tw:group/title tw:has-[[data-slot=inline-edit][data-expanded]]:gap-x-0";
const titleIconClass = "tw:flex tw:shrink-0 tw:items-center tw:group-has-[[data-slot=inline-edit]]/title-row:h-9";
const titleCompactIconClass = "tw:flex tw:shrink-0 tw:items-center tw:group-has-[[data-slot=inline-edit]]/title-row:h-7";
const titleClass = "tw:m-0 tw:text-3xl tw:font-semibold tw:leading-9 tw:text-foreground tw:break-words tw:has-[[data-slot=inline-edit]]:min-w-0 tw:has-[[data-expanded]]:flex-1";
const titleCompactClass = "tw:m-0 tw:text-xl tw:font-semibold tw:leading-7 tw:text-foreground tw:break-words tw:has-[[data-slot=inline-edit]]:min-w-0 tw:has-[[data-expanded]]:flex-1";
//#endregion
//#region src/blocks/PageHeader/PageHeaderDetail.tsx
/**
* Page header for one entity with a parent hierarchy worth breadcrumbing.
*
* - Nest inside `PageLayout.Header`.
* - A page with no parent hierarchy uses `PageHeader.Index`, even for one entity.
*
* @see RECIPES.md#list-detail
*/
const PageHeaderDetail = ({ ref, className, breadcrumbs, overflowActions, title, titleIcon, badges, description, compact = false, primaryAction, secondaryAction, headerNav, fullWidthDescription = false, ...props }) => {
	const iconClass = compact ? titleCompactIconClass : titleIconClass;
	const lineClass = compact ? titleCompactLineClass : titleLineClass;
	const titleRow = /* @__PURE__ */ jsxs("div", {
		className: cn(titleRowClass, "tw:min-w-64 tw:flex-1 tw:pr-2 tw:has-[[data-expanded]]:pr-0"),
		children: [
			titleIcon && /* @__PURE__ */ jsx("span", {
				className: iconClass,
				children: titleIcon
			}),
			/* @__PURE__ */ jsx("h1", {
				className: compact ? titleCompactClass : titleClass,
				children: title
			}),
			badges && /* @__PURE__ */ jsx("div", {
				className: cn("tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:group-has-[[data-slot=inline-edit]]/title-row:empty:hidden tw:group-has-[[data-slot=inline-edit]]/title-row:has-[>:only-child:empty]:hidden tw:[&_[data-slot=badge]]:rounded-full tw:[&_[data-slot=badge]]:normal-case tw:[&_[data-slot=badge]]:tracking-normal tw:[&_[data-slot=badge]]:py-0", lineClass, "tw:transition-opacity tw:duration-150 tw:group-has-[[data-slot=inline-edit][data-settled]]/title:starting:opacity-0 tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:hidden"),
				children: badges
			})
		]
	});
	const actions = (primaryAction || secondaryAction) && /* @__PURE__ */ jsxs("div", {
		className: cn("tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2", "tw:transition-opacity tw:duration-150 tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:invisible tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:w-0 tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:flex-nowrap tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:overflow-hidden tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:opacity-0"),
		children: [secondaryAction, primaryAction]
	});
	const descriptionNode = description && /* @__PURE__ */ jsx("p", {
		className: "tw:m-0 tw:max-w-[800px] tw:break-words tw:text-sm tw:leading-5 tw:text-muted-foreground",
		children: description
	});
	return /* @__PURE__ */ jsxs("header", {
		ref,
		className: cn("tw:flex tw:w-full tw:flex-col tw:gap-4", className),
		...props,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-4",
				children: [/* @__PURE__ */ jsx("div", {
					className: "tw:min-w-0 tw:flex-1",
					children: breadcrumbs
				}), overflowActions && /* @__PURE__ */ jsx("div", {
					className: "tw:flex tw:shrink-0 tw:items-center tw:gap-2",
					children: overflowActions
				})]
			}),
			fullWidthDescription ? /* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:w-full tw:flex-col tw:gap-2",
				children: [/* @__PURE__ */ jsxs("div", {
					className: cn(titleGroupClass, "tw:flex tw:w-full tw:flex-wrap tw:items-start tw:justify-between tw:gap-x-4 tw:gap-y-3"),
					children: [titleRow, actions]
				}), descriptionNode]
			}) : /* @__PURE__ */ jsxs("div", {
				className: cn(titleGroupClass, "tw:flex tw:w-full tw:flex-wrap tw:items-start tw:justify-between tw:gap-x-4 tw:gap-y-3"),
				children: [/* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:min-w-64 tw:flex-1 tw:flex-col tw:gap-2 tw:pr-2 tw:has-[[data-expanded]]:pr-0",
					children: [/* @__PURE__ */ jsxs("div", {
						className: titleRowClass,
						children: [
							titleIcon && /* @__PURE__ */ jsx("span", {
								className: iconClass,
								children: titleIcon
							}),
							/* @__PURE__ */ jsx("h1", {
								className: compact ? titleCompactClass : titleClass,
								children: title
							}),
							badges && /* @__PURE__ */ jsx("div", {
								className: cn("tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:group-has-[[data-slot=inline-edit]]/title-row:empty:hidden tw:group-has-[[data-slot=inline-edit]]/title-row:has-[>:only-child:empty]:hidden tw:[&_[data-slot=badge]]:rounded-full tw:[&_[data-slot=badge]]:normal-case tw:[&_[data-slot=badge]]:tracking-normal tw:[&_[data-slot=badge]]:py-0", lineClass, "tw:transition-opacity tw:duration-150 tw:group-has-[[data-slot=inline-edit][data-settled]]/title:starting:opacity-0 tw:group-has-[[data-slot=inline-edit][data-expanded]]/title:hidden"),
								children: badges
							})
						]
					}), descriptionNode]
				}), actions]
			}),
			headerNav
		]
	});
};
//#endregion
//#region src/blocks/PageHeader/PageHeaderIndex.tsx
/**
* Page header for top-level list / collection pages.
*
* - No breadcrumbs, no overflow menu.
* - Search and filters belong in `PageToolbar`, not here.
*/
const PageHeaderIndex = ({ ref, className, title, description, metadata, documentation, action, headerNav, fullWidthDescription = false, ...props }) => {
	const subtitle = metadata ?? description;
	const hasActions = Boolean(documentation || action);
	return /* @__PURE__ */ jsxs("header", {
		ref,
		className: cn("tw:flex tw:w-full tw:flex-col tw:gap-4", className),
		...props,
		children: [fullWidthDescription ? /* @__PURE__ */ jsxs("div", {
			className: "tw:flex tw:w-full tw:flex-col tw:gap-2",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:w-full tw:flex-wrap tw:items-start tw:justify-between tw:gap-x-4 tw:gap-y-3",
				children: [/* @__PURE__ */ jsx("div", {
					className: cn(titleRowClass, "tw:min-w-64 tw:flex-1 tw:pr-2"),
					children: /* @__PURE__ */ jsx("h1", {
						className: titleClass,
						children: title
					})
				}), hasActions && /* @__PURE__ */ jsxs("div", {
					className: "tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2",
					children: [documentation, action]
				})]
			}), subtitle && /* @__PURE__ */ jsx("p", {
				className: "tw:m-0 tw:max-w-[800px] tw:break-words tw:text-sm tw:leading-5 tw:text-muted-foreground",
				children: subtitle
			})]
		}) : /* @__PURE__ */ jsxs("div", {
			className: "tw:flex tw:w-full tw:items-start tw:justify-between tw:gap-4",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2 tw:pr-2",
				children: [/* @__PURE__ */ jsx("div", {
					className: titleRowClass,
					children: /* @__PURE__ */ jsx("h1", {
						className: titleClass,
						children: title
					})
				}), subtitle && /* @__PURE__ */ jsx("p", {
					className: "tw:m-0 tw:max-w-[800px] tw:break-words tw:text-sm tw:leading-5 tw:text-muted-foreground",
					children: subtitle
				})]
			}), hasActions && /* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:shrink-0 tw:items-center tw:gap-2",
				children: [documentation, action]
			})]
		}), headerNav]
	});
};
//#endregion
//#region src/blocks/PageHeader/PageHeader.tsx
/**
* Page header whose variant types enforce the page-header patterns.
*
* - Nest inside `PageLayout.Header`; pass `@keboola/design` `Button`s into the slots.
* - Other layouts: custom JSX in `PageLayout.Header`; routed tabs: `PageLayout.Tabs`.
*
* @see RECIPES.md#page-header-tabs
*/
const PageHeader = {
	/** List / collection pages. No breadcrumbs; `metadata` wins over `description`. */
	Index: PageHeaderIndex,
	/** Single-entity pages: required breadcrumbs, overflow menu, max 2 visible actions. */
	Detail: PageHeaderDetail
};
//#endregion
export { PageHeader };

//# sourceMappingURL=PageHeader-7B_kfjpW.js.map